import { toaster } from '@/shared/ui/toaster.component';
import {
  Avatar,
  Button,
  DataTable,
  DataTableColumn,
  Menu,
  Pagination,
  Tag,
  TabsComponent,
} from '@noorden-insurtech/alba-design-system';
import { useParams } from '@tanstack/react-router';
import { Ban, Building2, Edit, KeyRound, Mail, Phone, Plus, Send, UserCheck } from 'lucide-react';
import { useState } from 'react';
import type { User } from '../../../services';
import { useRequirePermissions } from '@/shared/hooks';
import { ADVISORY_ROLE_COLOR_SCHEME_MAPPING } from '../../../shared/constants/roles.constants';
import { SearchFilterField, SelectFilterField } from '../../../shared/ui';
import { Page, PageHeader } from '../../../shared/ui/page';
import { f, useFilters } from '../../../shared/utils';
import { formatPhone } from '../../../shared/utils/format-phone';
import { formatRegisterNumber } from '../../../shared/utils/format-register-number';
import { getRoleLabel } from '../../../shared/utils/get-role-label';
import { getUserStatus } from '../../../shared/utils/get-user-status';
import { EditUserModal } from '../../users/components/edit-user.modal';
import {
  useActivateUser,
  useAdminResetPassword,
  getResendFirstAccessErrorToast,
  useDeactivateUser,
  useResendFirstAccess,
} from '../../users/hooks';
import { useAdvisories, useAdvisoriesUsers } from '../hooks';
import { AdvisoryBrokersList } from './advisory-brokers-list.component';
import { CreateAdvisoryUserModal } from './create-advisory-user.modal';

const profileOptions = [
  { label: 'Todos', value: '' },
  { label: 'Assessor', value: 'advisory.advisor' },
  { label: 'Master', value: 'advisory.master' },
];

export function AdvisoriesIdPage() {
  const params = useParams({ strict: false });
  const id = params.id;
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const { hasPermission: canManageUsers } = useRequirePermissions({
    permissions: ['parties.advisories.users.write'],
  });
  const { hasPermission: canViewUsers } = useRequirePermissions({
    permissions: ['parties.advisories.users.read'],
  });
  const { hasPermission: canResendFirstAccess } = useRequirePermissions({
    permissions: [
      'identity.users.write',
      'parties.brokers.users.write',
      'parties.advisories.users.write',
    ],
  });
  const { hasPermission: canResetPassword } = useRequirePermissions({
    permissions: [
      'identity.users.write',
      'parties.brokers.users.write',
      'parties.advisories.users.write',
    ],
  });

  const { filters, debouncedFilters, setFilters, registerFilter } = useFilters({
    search: f.string().withDefault('').withDebounce(500),
    status: f.string().withDefault(''),
    pageNo: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(5),
    partyCode: f.string().withDefault(id || ''),
  });

  const { data, isLoading, error, refetch } = useAdvisoriesUsers({
    limit: debouncedFilters.pageSize,
    page: debouncedFilters.pageNo,
    search: debouncedFilters.search,
    partyCode: debouncedFilters.partyCode,
  });

  const { data: resultAdvisories } = useAdvisories(params);
  const advisory = resultAdvisories?.advisories[0];

  const { mutateAsync: deactivateUser, isPending: isDeactivating } =
    useDeactivateUser();
  const { mutateAsync: activateUser, isPending: isActivating } =
    useActivateUser();
  const { mutateAsync: resendFirstAccess, isPending: isResendingFirstAccess } =
    useResendFirstAccess();
  const { mutateAsync: adminResetPassword, isPending: isResettingPassword } =
    useAdminResetPassword();

  const columns: DataTableColumn<User>[] = [
    {
      accessorKey: 'name',
      header: 'Nome',
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className='flex items-center gap-3'>
            <Avatar.Root size='md' shape='circle'>
              <Avatar.Fallback />
            </Avatar.Root>
            <div className='flex flex-col gap-1'>
              <span className='font-gotham font-bold text-alba-black-500'>
                {user.name}
              </span>
              <div className='flex items-center gap-1'>
                <Mail className='w-4 h-4 text-alba-gray-600' />
                <span className='text-alba-gray-600 text-xs'>{user.email}</span>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const user = row.original;
        const statusText = getUserStatus(user.status, user.deactivated_at);
        const isActive = statusText !== 'Inativo';
        return (
          <div className='flex items-center gap-2'>
            <span
              className={`w-2.5 h-2.5 rounded-full ${
                isActive ? 'bg-alba-success-green-500' : 'bg-alba-gray-400'
              }`}
            />
            <span className='text-alba-gray-700 text-sm'>
              {statusText}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'groupName',
      header: 'Perfil',
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className=''>
            <Tag.Root
              variant='outline'
              colorScheme={
                ADVISORY_ROLE_COLOR_SCHEME_MAPPING[user.role]
              }
              size='lg'
            >
              <Tag.Label>{getRoleLabel(user.groupName)}</Tag.Label>
            </Tag.Root>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      size: 50,
      sortable: false,
      cell: ({ row }) => {
        const user = row.original;
        if (!canManageUsers && !canResendFirstAccess && !canResetPassword) return null;
        return (
          <div className='flex justify-end'>
            <Menu.Root
              key={`${user.email}-${user.status}-${user.deactivated_at ?? ''}`}
            >
              <Menu.Trigger />
              <Menu.Content>
                {canManageUsers && (
                  <Menu.Item
                    value='edit'
                    icon={<Edit size={16} />}
                    onSelect={() => setEditingUser(user)}
                  >
                    Editar
                  </Menu.Item>
                )}
                {canResendFirstAccess &&
                  user.status === 'FORCE_CHANGE_PASSWORD' && (
                    <Menu.Item
                      value='resend'
                      icon={<Send size={16} />}
                      disabled={isResendingFirstAccess}
                      onSelect={async () => {
                        try {
                          await resendFirstAccess(user.email);
                          toaster.create({
                            type: 'success',
                            title: 'Convite reenviado',
                            description:
                              'O link do primeiro acesso foi reenviado por email.',
                          });
                          await refetch();
                        } catch (error) {
                          toaster.create(getResendFirstAccessErrorToast(error));
                        }
                      }}
                    >
                      Reenviar convite
                    </Menu.Item>
                  )}
                {canResetPassword &&
                  getUserStatus(user.status, user.deactivated_at) === 'Ativo' && (
                    <Menu.Item
                      value='reset-password'
                      icon={<KeyRound size={16} />}
                      disabled={isResettingPassword}
                      onSelect={async () => {
                        try {
                          await adminResetPassword(user.email);
                          toaster.create({
                            type: 'success',
                            title: 'Link enviado',
                            description:
                              'O usuário receberá um email com link para redefinir a senha.',
                          });
                          await refetch();
                        } catch (error) {
                          const msg = (
                            error instanceof Error
                              ? error.message
                              : 'Falha ao enviar link de redefinição'
                          ).trim();
                          toaster.create({
                            type: 'error',
                            title: 'Erro ao enviar link',
                            description: msg,
                          });
                        }
                      }}
                    >
                      Resetar senha
                    </Menu.Item>
                  )}
                {canManageUsers &&
                getUserStatus(user.status, user.deactivated_at) !== 'Inativo' ? (
                  <Menu.Item
                    value='disable'
                    icon={<Ban size={16} />}
                    disabled={isDeactivating}
                    onSelect={async () => {
                      try {
                        await deactivateUser(user.email);
                        toaster.create({
                          type: 'success',
                          title: 'Usuário desativado',
                          description: 'O usuário foi desativado com sucesso.',
                        });
                        await refetch();
                      } catch (error) {
                        const msg = (
                          error instanceof Error
                            ? error.message
                            : 'Falha ao desativar usuário'
                        ).trim();
                        toaster.create({
                          type: 'error',
                          title: 'Erro ao desativar usuário',
                          description: msg,
                        });
                      }
                    }}
                  >
                    Desativar
                  </Menu.Item>
                ) : (
                  <Menu.Item
                    value='activate'
                    icon={<UserCheck size={16} />}
                    disabled={isActivating}
                    onSelect={async () => {
                      try {
                        await activateUser(user.email);
                        toaster.create({
                          type: 'success',
                          title: 'Usuário ativado',
                          description: 'O usuário foi ativado com sucesso.',
                        });
                        await refetch();
                      } catch (error) {
                        const msg = (
                          error instanceof Error
                            ? error.message
                            : 'Falha ao ativar usuário'
                        ).trim();
                        toaster.create({
                          type: 'error',
                          title: 'Erro ao ativar usuário',
                          description: msg,
                        });
                      }
                    }}
                  >
                    Ativar
                  </Menu.Item>
                )}
              </Menu.Content>
            </Menu.Root>
          </div>
        );
      },
    },
  ];

  const usersTabContent = (
    <>
      <div className='mb-6'>
        <h1 className='text-xl text-alba-gray-800 mt-2'>
          Usuários da Assessoria
        </h1>
        <p className='text-alba-gray-600 text-sm'>
          Gerencie os usuários vinculados a esta assessoria
        </p>
      </div>

      <PageHeader.Filters className='mt-2'>
        <div className='mb-6 flex flex-col gap-3 md:flex-row md:items-center md:gap-4'>
          <SearchFilterField
            {...registerFilter('search')}
            placeholder='Buscar por nome, CPF ou email...'
            className='w-full md:w-1/3'
          />
          <SelectFilterField
            {...registerFilter('status')}
            items={profileOptions}
            placeholder='Perfil'
            onValueChange={() => setFilters({ ...filters, pageNo: 1 })}
            className='w-full md:w-48 md:flex-shrink-0'
          />
          {canManageUsers && (
            <Button
              variant='primary'
              size='md'
              onClick={() => setIsCreateUserModalOpen(true)}
              className='w-full md:ml-auto md:w-auto'
            >
              <Plus className='h-5 w-5' strokeWidth={2} />
              Adicionar usuário
            </Button>
          )}
        </div>
      </PageHeader.Filters>

      <Page.Content>
        {error ? (
          <div className='py-8 text-center text-alba-error-red-500'>
            Erro ao carregar usuários da assessorias. Tente novamente.
          </div>
        ) : (
          <>
            <div className='space-y-3 md:hidden'>
              {isLoading ? (
                Array.from({ length: Math.min(filters.pageSize, 3) }).map((_, index) => (
                  <div
                    key={index}
                    className='h-28 animate-pulse rounded-2xl bg-alba-gray-200'
                  />
                ))
              ) : (
                (data?.users || []).map(user => {
                  const statusText = getUserStatus(
                    user.status,
                    user.deactivated_at
                  );
                  const isActive = statusText !== 'Inativo';
                  return (
                    <article
                      key={user.email}
                      className='rounded-2xl bg-white p-3 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'
                    >
                      <div className='flex items-start justify-between gap-2'>
                        <div className='flex min-w-0 items-start gap-2'>
                          <Avatar.Root size='md' shape='circle'>
                            <Avatar.Fallback />
                          </Avatar.Root>
                          <div className='min-w-0'>
                            <p className='truncate font-gotham font-bold text-alba-black-500'>
                              {user.name}
                            </p>
                            <p className='mt-1 truncate text-xs text-alba-gray-600'>
                              {user.email}
                            </p>
                          </div>
                        </div>
                        {(canManageUsers || canResendFirstAccess || canResetPassword) && (
                          <Menu.Root
                            key={`${user.email}-${user.status}-${user.deactivated_at ?? ''}`}
                          >
                            <Menu.Trigger />
                            <Menu.Content>
                              {canManageUsers && (
                                <Menu.Item
                                  value='edit'
                                  icon={<Edit size={16} />}
                                  onSelect={() => setEditingUser(user)}
                                >
                                  Editar
                                </Menu.Item>
                              )}
                            </Menu.Content>
                          </Menu.Root>
                        )}
                      </div>

                      <div className='mt-3 flex items-center justify-between gap-2'>
                        <Tag.Root
                          variant='outline'
                          colorScheme={
                            ADVISORY_ROLE_COLOR_SCHEME_MAPPING[user.role]
                          }
                          size='sm'
                        >
                          <Tag.Label>{getRoleLabel(user.groupName)}</Tag.Label>
                        </Tag.Root>
                        <div className='flex items-center gap-2'>
                          <span
                            className={`h-2.5 w-2.5 rounded-full ${
                              isActive ? 'bg-alba-success-green-500' : 'bg-alba-gray-400'
                            }`}
                          />
                          <span className='text-xs text-alba-gray-700'>
                            {statusText}
                          </span>
                        </div>
                      </div>
                    </article>
                  );
                })
              )}

              {!isLoading && !(data?.users || []).length && (
                <div className='rounded-2xl bg-white p-6 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
                  Nenhum usuário encontrado.
                </div>
              )}

              {(data?.pagination.totalPages || 0) > 0 && (
                <div className='flex justify-center'>
                  <Pagination
                    count={(data?.pagination.totalPages || 0) * filters.pageSize}
                    page={filters.pageNo}
                    pageSize={filters.pageSize}
                    totalItems={data?.pagination.total || 0}
                    pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                    onPageChange={page =>
                      setFilters({
                        ...filters,
                        pageNo: page.page,
                        pageSize: page.pageSize,
                      })
                    }
                    onPageSizeSelect={pageSize =>
                      setFilters({ ...filters, pageNo: 1, pageSize })
                    }
                  />
                </div>
              )}
            </div>

            <div className='hidden md:block'>
              <DataTable
                loading={isLoading}
                columns={columns}
                pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                totalPages={data?.pagination.totalPages || 0}
                data={data?.users || []}
                pageSize={filters.pageSize}
                totalItems={data?.pagination.total || 0}
                onPageChange={(page, pageSize) =>
                  setFilters({ ...filters, pageNo: page, pageSize })
                }
              />
            </div>
          </>
        )}
      </Page.Content>
    </>
  );

  const tabs = [
    ...(canViewUsers
      ? [{ value: 'users', label: 'Usuários', content: usersTabContent }]
      : []),
    {
      value: 'brokers',
      label: 'Grade de Corretores',
      content: <AdvisoryBrokersList advisoryId={id || ''} />,
    },
  ];

  const defaultTab = canViewUsers ? 'users' : 'brokers';
  const tabGridClass =
    tabs.length > 1 ? 'grid-cols-2' : 'grid-cols-1';

  return (
    <Page.Root>
      <Page.Breadcrumb
        items={[
          { label: 'Página Inicial', href: '/advisories' },
          { label: '...', href: '/' },
          { label: 'Detalhes da Assessoria', active: true },
        ]}
      />

      <PageHeader.Root>
        <PageHeader.Main>
          <PageHeader.TitleGroup>
            <div className='flex items-start gap-3 sm:gap-4'>
              <div className='shrink-0 self-start'>
                <div className='flex h-10 w-10 items-center justify-center rounded-full bg-alba-blue-500 mt-1 sm:mt-2 sm:h-16 sm:w-16'>
                  <Building2 className='h-5 w-5 text-white sm:h-8 sm:w-8' />
                </div>
              </div>
              <div className='flex min-w-0 flex-col'>
                <PageHeader.Title className='break-words text-base sm:text-[30px]'>
                  {advisory?.name}
                </PageHeader.Title>
                <p className='text-sm text-alba-gray-600'>
                  CNPJ: {formatRegisterNumber(advisory?.cnpj)}
                </p>
                <div className='flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4'>
                  <div className='flex items-center gap-1'>
                    <Mail className='w-4 h-4 text-alba-gray-600' />
                    <span className='break-all text-sm text-alba-gray-600'>
                      {advisory?.contactEmail}
                    </span>
                  </div>
                  <div className='flex items-center gap-1'>
                    <Phone className='w-4 h-4 text-alba-gray-600' />
                    <span className='text-sm text-alba-gray-600'>
                      {formatPhone(advisory?.contactPhone)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </PageHeader.TitleGroup>
        </PageHeader.Main>
      </PageHeader.Root>

      <TabsComponent.Root defaultValue={defaultTab} className='mt-2 w-full'>
        <TabsComponent.List className={`grid w-full ${tabGridClass}`}>
          {tabs.map(tab => (
            <TabsComponent.Trigger
              key={tab.value}
              value={tab.value}
              className='flex-1'
            >
              {tab.label}
            </TabsComponent.Trigger>
          ))}
          <TabsComponent.Indicator />
        </TabsComponent.List>

        {tabs.map(tab => (
          <TabsComponent.Content key={tab.value} value={tab.value}>
            {tab.content}
          </TabsComponent.Content>
        ))}
      </TabsComponent.Root>

      <CreateAdvisoryUserModal
        isOpen={isCreateUserModalOpen}
        onClose={() => setIsCreateUserModalOpen(false)}
        onSuccess={() => {
          setIsCreateUserModalOpen(false);
          refetch();
        }}
        advisoryId={id || ''}
        advisoryName={advisory?.name || ''}
      />

      <EditUserModal
        userId={null}
        initialUser={editingUser}
        isOpen={Boolean(editingUser)}
        onClose={() => setEditingUser(null)}
        onSuccess={() => {
          setEditingUser(null);
          refetch();
        }}
      />
    </Page.Root>
  );
}
